/**
 * sl-m82f.4.2 + sl-m82f.4.3: Shell component shared by every feature tab.
 *
 * Tabs only describe:
 *   1. their three summary cards
 *   2. the full (pre-filter) row list
 *   3. the table header + labels
 *
 * The shell takes care of:
 *   - Rendering the `TabSummaryCards`
 *   - Running the drill-down filter (`useDrillDown`)
 *   - Rendering the "By User" / "By Model" `DrillDownSelect`s
 *   - Rendering the filtered `UsageTable`
 *   - Loading / error states
 *
 * Keeping this logic in one place eliminates five copies of the same
 * scaffolding from the tab files — DRY.
 */
import { useI18n } from '@affine/i18n';
import { type ReactNode, useEffect } from 'react';

import { DrillDownSelect } from './drill-down-select';
import { errorStyle, filterRow, loadingStyle } from './styles';
import { type SummaryCardData, TabSummaryCards } from './tab-summary-card';
import type { BreakdownRow } from './types';
import { UsageTable } from './usage-table';
import { useDrillDown } from './use-drill-down';

export interface FeatureTabShellProps {
  summary: readonly [SummaryCardData, SummaryCardData, SummaryCardData];
  rows: BreakdownRow[];
  nameHeader: string;
  countLabel?: string;
  emptyLabel?: string;
  isLoading?: boolean;
  error?: string | null;
  /** Hide the "By Model" drill-down when the tab has no per-model rows. */
  hideModelFilter?: boolean;
  /** Hide the "By User" drill-down when the tab has no per-user rows. */
  hideUserFilter?: boolean;
  /** Override dropdown option labels (e.g. when the row's `name` isn't the user). */
  userLabeler?: (row: BreakdownRow) => string;
  modelLabeler?: (row: BreakdownRow) => string;
  /**
   * Bumped whenever the parent wants to force-reset the drill-down filters
   * (e.g. when the month selector changes). Change the value and the
   * shell resets both filters.
   */
  resetSignal?: string;
  /** Custom name-cell renderer forwarded to `UsageTable` (doc-keyed tabs). */
  renderName?: (row: BreakdownRow) => ReactNode;
}

export const FeatureTabShell = ({
  summary,
  rows,
  nameHeader,
  countLabel,
  emptyLabel,
  isLoading = false,
  error = null,
  hideModelFilter = false,
  hideUserFilter = false,
  userLabeler,
  modelLabeler,
  resetSignal,
  renderName,
}: FeatureTabShellProps) => {
  const t = useI18n();
  const drill = useDrillDown(rows, { userLabeler, modelLabeler });

  // Reset filters when the parent signals a period change.
  useEffect(() => {
    drill.reset();
    // We only want to reset when `resetSignal` changes, not on every
    // drill.reset identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  const byUserLabel =
    t['com.streamlain.settings.workspace.ai.analytics.drill.by-user']?.() ??
    'By User';
  const byModelLabel =
    t['com.streamlain.settings.workspace.ai.analytics.drill.by-model']?.() ??
    'By Model';
  const allUsersLabel =
    t['com.streamlain.settings.workspace.ai.analytics.drill.all-users']?.() ??
    'All users';
  const allModelsLabel =
    t['com.streamlain.settings.workspace.ai.analytics.drill.all-models']?.() ??
    'All models';

  return (
    <>
      <TabSummaryCards cards={summary} />

      <div style={filterRow}>
        {!hideUserFilter && (
          <DrillDownSelect
            label={byUserLabel}
            allLabel={allUsersLabel}
            options={drill.userOptions}
            selected={drill.userFilter}
            onSelect={drill.setUserFilter}
            hideWhenEmpty
          />
        )}
        {!hideModelFilter && (
          <DrillDownSelect
            label={byModelLabel}
            allLabel={allModelsLabel}
            options={drill.modelOptions}
            selected={drill.modelFilter}
            onSelect={drill.setModelFilter}
            hideWhenEmpty
          />
        )}
      </div>

      {isLoading && <div style={loadingStyle}>Loading…</div>}
      {error && <div style={errorStyle}>{error}</div>}
      {!isLoading && !error && (
        <UsageTable
          rows={drill.filtered}
          nameHeader={nameHeader}
          countLabel={countLabel}
          emptyLabel={emptyLabel}
          renderName={renderName}
        />
      )}
    </>
  );
};
